/**
 * Funnel state machine for the audition onboarding flow (spec §4).
 * Pure data + lookups — no I/O — so both the API and later admin UI share
 * one source of truth for what moves a prospect forward.
 *
 * Slice 1 only fires `wizard_signup_completed`; the rest of the table is
 * exercised by the payments/booking/admin slices but is defined now so the
 * enum, roles, and transitions never drift between slices.
 */
export type AuditionStage =
  | 'wizard_in_progress'
  | 'audition_completed'
  | 'paid_pending_call'
  | 'vibe_call_booked'
  | 'payment_unlocked'
  | 'client'
  | 'rejected'

export type AuditionEvent =
  | 'wizard_signup_completed'
  | 'payment_succeeded'
  | 'call_booked'
  | 'payment_unlocked_by_admin'
  | 'approved_by_admin'
  | 'rejected_by_admin'

const TRANSITIONS: Record<
  AuditionStage,
  Partial<Record<AuditionEvent, AuditionStage>>
> = {
  wizard_in_progress: { wizard_signup_completed: 'audition_completed' },
  audition_completed: {
    payment_succeeded: 'paid_pending_call', // fast lane
    call_booked: 'vibe_call_booked', // consideration lane
    // Early unlock (LG-414): accepting an obvious fit at Applied skips the
    // call entirely. Unlock IS the approval for anyone who has not paid.
    payment_unlocked_by_admin: 'payment_unlocked',
    rejected_by_admin: 'rejected',
  },
  paid_pending_call: {
    call_booked: 'vibe_call_booked',
    // Early approve (LG-414): a fast-lane payer who never wants a call had
    // no way out of this stage at all — `approved_by_admin` was only legal
    // from `vibe_call_booked`, so skipping the call stranded them forever.
    // The PaymentIntent guard in `admin.auditions.approve` still holds, so
    // this can only ever fire for someone who really did pay.
    approved_by_admin: 'client',
    rejected_by_admin: 'rejected',
  },
  vibe_call_booked: {
    approved_by_admin: 'client', // fast lane: already paid
    payment_unlocked_by_admin: 'payment_unlocked', // consideration lane: unlock IS the approval
    rejected_by_admin: 'rejected',
  },
  payment_unlocked: {
    payment_succeeded: 'client',
    // "Keep the call anyway" (LG-414). Early unlock does not withdraw an
    // open call offer, so the applicant can still book one afterwards.
    // Without this self-loop that booking would be refused outright; the
    // call is informational from here on, so the stage stays put.
    call_booked: 'payment_unlocked',
    rejected_by_admin: 'rejected',
  },
  // Self-loop only, for the same reason as `payment_unlocked` above: a call
  // booked after an early approve is kept, not refused. `isTerminalStage`
  // asks whether anything moves the stage ELSEWHERE, so this stays terminal.
  client: { call_booked: 'client' },
  rejected: {},
}

/**
 * True when no event can move this stage to a DIFFERENT one — `client` and
 * `rejected` today. Derived from the table so a future terminal stage is
 * covered the moment it is added, rather than needing every guard to be
 * found and updated (which is how a rejected applicant kept being able to
 * book calls).
 *
 * "Different", not "any": `client` carries a `call_booked` self-loop so a
 * call booked after an early approve survives. A self-loop advances nothing,
 * so counting it would quietly re-open every terminal-stage guard in the
 * funnel — the retry action, the booking job, and the proposal email send.
 */
export function isTerminalStage(stage: AuditionStage): boolean {
  // `?? {}` is fail-safe rather than defensive noise: an unrecognised stage
  // reads as terminal, so a guard built on this refuses instead of letting
  // an unknown state through.
  const exits = Object.values(TRANSITIONS[stage] ?? {})
  return exits.every((next) => next === stage)
}

export function transition(
  stage: AuditionStage,
  event: AuditionEvent,
): AuditionStage | null {
  return TRANSITIONS[stage][event] ?? null
}

/**
 * May someone at this stage book an intro call?
 *
 * Not the same question as `transition(stage, 'call_booked')`, which says
 * where a booking MOVES them, and the two disagree on exactly one stage.
 * `vibe_call_booked` has no `call_booked` exit because reaching it means a
 * call was booked — but two lanes (`audition_completed` and
 * `paid_pending_call`) merge into it, so when that call is cancelled no
 * transition could take them back: the origin lane is gone. Rebooking from
 * there is ordinary behaviour, not an attack, so this admits it while
 * `transition` correctly moves nothing.
 *
 * This is the access-control list for the public cal.com page and for the
 * in-app slot picker. Both used to hand-roll it from `transition` and gave
 * different answers (LG-940). Whether the applicant already HOLDS a live
 * call is a separate fact the caller settles first — `bookingGate` refuses a
 * second booking before ever asking this. `isTerminalStage` is the precedent
 * for a question the table answers without being a transition.
 */
export function acceptsCallBooking(stage: AuditionStage): boolean {
  return (
    transition(stage, 'call_booked') !== null || stage === 'vibe_call_booked'
  )
}

// Full stage list, derived from the transition table so it can never drift
// from it. Used to assert parity against `auditionStageEnum` (db enum).
export const AUDITION_STAGES = Object.keys(TRANSITIONS) as AuditionStage[]

/**
 * The Clerk roles that exist ONLY as audition-funnel stage markers — they
 * grant no access on their own (unlike 'admin'/'client'). Single source of
 * truth: lib/auth.ts derives its UserRole union and funnel-role guard from
 * this list, and admin archive derives its "safe to clear" set from it.
 * STAGE_ROLES below is typed against it, so a new stage role added there
 * without updating this list is a compile error, not silent drift.
 */
export const AUDITION_FUNNEL_ROLES = [
  'audition-completed',
  'paid-pending-call',
  'vibe-call-booked',
  'payment-unlocked',
] as const
export type AuditionFunnelRole = (typeof AUDITION_FUNNEL_ROLES)[number]

const STAGE_ROLES: Record<AuditionStage, AuditionFunnelRole | 'client' | null> =
  {
    wizard_in_progress: null, // no account yet — CIO person only
    audition_completed: 'audition-completed',
    paid_pending_call: 'paid-pending-call',
    vibe_call_booked: 'vibe-call-booked',
    payment_unlocked: 'payment-unlocked',
    client: 'client',
    rejected: null, // cooldown flag handled in the admin slice
  }

export function roleForStage(stage: AuditionStage) {
  return STAGE_ROLES[stage]
}
